import { useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { MenuItems } from '@/constants/menu';
import { useDemoContext } from '@/hooks/DemoContext';
import { useAuth } from '@/hooks/useAuth';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';
import { useUserStore } from '@/store/useStore';

import type { MenuItemProps } from '@/constants/menu';

/**
 * Logic shared by `SidebarDesktop` and `SidebarMobile` and their subcomponents.
 *
 * The `<dialog>` open/close state is deliberately absent: the desktop sidebar is not
 * dismissible, so that state lives in `SidebarMobile` only.
 */
const useSidebarMenu = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { isDemo } = useDemoContext();
  const advisorUser = useUserStore((state) => state.user);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const userRole = user?.type;

  const isMenuItemEnabled = (menuItem: MenuItemProps) => Boolean(userRole && menuItem.roles.includes(userRole));

  const selectUserMenuItem = (item: MenuItemProps) => {
    if (item.icon === 'logout') {
      logout();
    } else {
      navigate(item.path);
    }

    void TRACKING_INSTANCE.triggerEvent('clic', undefined, item.tracking);
  };

  const openUserMenu = () => setIsUserMenuOpen(true);
  const closeUserMenu = () => setIsUserMenuOpen(false);

  return {
    user,
    menuItems: MenuItems,
    isMenuItemEnabled,
    isDemo,
    hasAdvisorView: Boolean(advisorUser?.advisorView),
    isUserMenuOpen,
    openUserMenu,
    closeUserMenu,
    selectUserMenuItem,
  };
};

export default useSidebarMenu;
